import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Transaction, TransactionCreate, TransactionFilters, Journal, TransactionStatus } from '../types'

const QUERY_KEY = 'transactions'

export function useTransactions(filters: TransactionFilters = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: async () => {
      let query = supabase
        .from('transactions')
        .select(`
          *,
          journal:journals(*),
          lines:transaction_lines(
            *,
            residence_chart_account:residence_chart_accounts(
              id,
              custom_code,
              custom_name,
              chart_account:chart_accounts(id, code, name)
            ),
            unit:units(id, unit_number),
            owner:owners(id, first_name, last_name)
          )
        `)
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false })

      if (filters.residence_id) query = query.eq('residence_id', filters.residence_id)
      if (filters.fiscal_year_id) query = query.eq('fiscal_year_id', filters.fiscal_year_id)
      if (filters.journal_id) query = query.eq('journal_id', filters.journal_id)
      if (filters.status) query = query.eq('status', filters.status as TransactionStatus)
      if (filters.date_from) query = query.gte('transaction_date', filters.date_from)
      if (filters.date_to) query = query.lte('transaction_date', filters.date_to)
      if (filters.search) {
        query = query.or(`description.ilike.%${filters.search}%,piece_number.ilike.%${filters.search}%`)
      }

      const { data, error } = await query.limit(100)
      if (error) throw error

      // Mapper residence_chart_account.chart_account vers account pour compatibilité
      return (data || []).map(tx => ({
        ...tx,
        lines: tx.lines?.map((line: any) => ({
          ...line,
          account: line.residence_chart_account?.chart_account,
        })),
      })) as Transaction[]
    },
    enabled: !!filters.residence_id,
  })
}

export function useTransaction(id?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          journal:journals(*),
          lines:transaction_lines(
            *,
            residence_chart_account:residence_chart_accounts(
              id,
              custom_code,
              custom_name,
              chart_account:chart_accounts(id, code, name)
            ),
            owner:owners(id, first_name, last_name),
            unit:units(id, unit_number)
          )
        `)
        .eq('id', id!)
        .single()

      if (error) throw error

      // Mapper residence_chart_account.chart_account vers account pour compatibilité
      if (data) {
        return {
          ...data,
          lines: data.lines?.map((line: any) => ({
            ...line,
            account: line.residence_chart_account?.chart_account,
          })),
        } as Transaction
      }
      return data as Transaction
    },
    enabled: !!id,
  })
}

export function useJournals() {
  return useQuery({
    queryKey: ['journals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('journals')
        .select('*')
        .order('code')

      if (error) throw error
      return data as Journal[]
    },
  })
}

export function useCreateTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: TransactionCreate) => {
      // Créer la transaction
      const { data: transaction, error: txError } = await supabase
        .from('transactions')
        .insert({
          journal_id: data.journal_id,
          residence_id: data.residence_id,
          fiscal_year_id: data.fiscal_year_id,
          transaction_date: data.transaction_date,
          description: data.description,
          reference: data.reference,
          status: 'draft',
        })
        .select()
        .single()

      if (txError) throw txError

      // Créer les lignes
      if (data.lines.length > 0) {
        const { error: linesError } = await supabase.from('transaction_lines').insert(
          data.lines.map((line) => ({
            transaction_id: transaction.id,
            residence_chart_account_id: line.residence_chart_account_id,
            debit: line.debit,
            credit: line.credit,
            label: line.label,
            owner_id: line.owner_id,
            unit_id: line.unit_id,
            fund_call_item_id: line.fund_call_item_id,
            payment_id: line.payment_id,
          }))
        )
        if (linesError) throw linesError
      }

      return transaction as Transaction
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Transaction> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('transactions')
        .update(data)
        .eq('id', id)
        .eq('status', 'draft') // Only draft can be updated
        .select()
        .single()

      if (error) throw error
      return result as Transaction
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useUpdateTransactionWithLines() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      transaction,
      lines,
    }: {
      id: string
      transaction: {
        journal_id: string
        transaction_date: string
        description: string
        reference?: string
      }
      lines: {
        residence_chart_account_id: string
        debit: number
        credit: number
        label?: string
        owner_id?: string
        unit_id?: string
      }[]
    }) => {
      // 1. Update transaction
      const { error: txError } = await supabase
        .from('transactions')
        .update({
          journal_id: transaction.journal_id,
          transaction_date: transaction.transaction_date,
          description: transaction.description,
          reference: transaction.reference,
        })
        .eq('id', id)
        .eq('status', 'draft')

      if (txError) throw txError

      // 2. Delete existing lines
      const { error: deleteError } = await supabase
        .from('transaction_lines')
        .delete()
        .eq('transaction_id', id)

      if (deleteError) throw deleteError

      // 3. Insert new lines
      if (lines.length > 0) {
        const { error: linesError } = await supabase.from('transaction_lines').insert(
          lines.map((line) => ({
            transaction_id: id,
            residence_chart_account_id: line.residence_chart_account_id,
            debit: line.debit,
            credit: line.credit,
            label: line.label,
            owner_id: line.owner_id,
            unit_id: line.unit_id,
          }))
        )
        if (linesError) throw linesError
      }

      return { id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useValidateTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('validate_transaction', {
        p_transaction_id: id,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useReverseTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      transactionId,
      reversalDate,
    }: {
      transactionId: string
      reversalDate?: string
    }) => {
      const { data, error } = await supabase.rpc('reverse_transaction', {
        p_transaction_id: transactionId,
        p_reversal_date: reversalDate || new Date().toISOString().split('T')[0],
      })
      if (error) throw error
      return data as string // new transaction id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id)
        .eq('status', 'draft')

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}
